import { Tooltip, Typography } from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import {
  WasteScheduleAdherenceQuery,
  WasteScheduleAdherenceStatus,
  WasteServiceKind,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import {
  formatInvoiceDate,
  formatInvoiceMoney,
  IMPORT_VENDOR_LABELS,
  NO_VALUE,
} from "routes/Customer/Waste/Invoices/utils";

import { InvoiceLinks } from "./InvoiceLinks";
import {
  ASSOCIATED_BILLING,
  facilityLabel,
  formatDaysOutside,
  formatWindow,
  KIND_LABELS,
  kindOptionLabel,
  STATUS_LABELS,
  statusOptionLabel,
  UNKNOWN_KIND_LABEL,
} from "./labels";

type Exception = WasteScheduleAdherenceQuery["wasteScheduleAdherence"]["items"][number];

/** A graded result carrying the grid's own row key. */
export type ExceptionRow = Exception & { id: string };

/**
 * Colour states what the audit concluded. Amber: service the schedule did not
 * plan, a finding against the vendor. Grey: nothing settled — an unserved
 * window may be a missed pickup or a month not yet imported.
 */
export const STATUS_COLOUR: Record<WasteScheduleAdherenceStatus, StatusChipColor> = {
  [WasteScheduleAdherenceStatus.WithinSchedule]: "success",
  [WasteScheduleAdherenceStatus.OutsideSchedule]: "warning",
  [WasteScheduleAdherenceStatus.UnscheduledService]: "warning",
  [WasteScheduleAdherenceStatus.NoServiceShown]: "default",
  [WasteScheduleAdherenceStatus.NeedsReview]: "default",
};

export const COLUMNS: OmnisearchGridColDef<ExceptionRow>[] = [
  {
    field: "vendor",
    headerName: "Vendor",
    width: 116,
    // Only the vendors this tenant has invoices from, as the invoice list offers them.
    filterKeyType: "wasteInvoiceVendor",
    valueGetter: (_value, row) => IMPORT_VENDOR_LABELS[row.vendor],
  },
  {
    field: "status",
    headerName: "Status",
    width: 160,
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(WasteScheduleAdherenceStatus),
    enumPresentationFunction: statusOptionLabel,
    renderCell: ({ row }) => (
      <StatusChip color={STATUS_COLOUR[row.status]} label={STATUS_LABELS[row.status]} />
    ),
  },
  {
    field: "store",
    headerName: "Facility",
    width: 220,
    // Narrowed through the facility picker above the grid, not typed here.
    filterable: false,
    valueGetter: (_value, row) =>
      facilityLabel(row.facilityName, row.customerFacilityId) ?? NO_VALUE,
  },
  {
    field: "kind",
    headerName: "Service",
    width: 168,
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(WasteServiceKind),
    enumPresentationFunction: kindOptionLabel,
    valueGetter: (_value, row) => (row.kind ? KIND_LABELS[row.kind] : UNKNOWN_KIND_LABEL),
  },
  {
    field: "scheduledWindowEnd",
    headerName: "Scheduled",
    // Fits the longest window a vendor publishes: two dates and a dash.
    width: 210,
    // Sortable, but not a filter: a reader narrows by the date service happened.
    filterable: false,
    valueGetter: (_value, row) =>
      row.occurrence
        ? formatWindow(row.occurrence.scheduledWindowStart, row.occurrence.scheduledWindowEnd)
        : NO_VALUE,
  },
  {
    field: "serviceDate",
    headerName: "Service date",
    width: 104,
    filterKeyType: "date",
    valueGetter: (_value, row) => formatInvoiceDate(row.visit?.serviceDate),
  },
  {
    field: "daysOutside",
    headerName: "Timing",
    width: 112,
    filterKeyType: "number",
    valueGetter: (_value, row) => formatDaysOutside(row.daysOutside),
  },
  {
    field: "billedAmount",
    headerName: "Billed",
    description: ASSOCIATED_BILLING,
    width: 92,
    align: "right",
    headerAlign: "right",
    filterable: false,
    valueGetter: (_value, row) => row.visit?.billedAmount ?? null,
    valueFormatter: (value: string | null) =>
      value == null ? NO_VALUE : formatInvoiceMoney(value),
  },
  {
    field: "serviceOrderNumber",
    headerName: "Order number",
    width: 130,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) =>
      row.visit?.serviceOrderNumber ?? row.occurrence?.vendorServiceOrderNumber ?? NO_VALUE,
  },
  {
    field: "manifestNumber",
    headerName: "Manifest",
    width: 130,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => row.visit?.manifestNumber ?? NO_VALUE,
  },
  {
    field: "invoices",
    headerName: "Invoices",
    width: 150,
    sortable: false,
    filterable: false,
    // What a reader quotes back to the vendor, and what they open to check it.
    // One visit is sometimes billed across two.
    valueGetter: (_value, row) => row.visit?.invoiceNumbers.join(", ") ?? NO_VALUE,
    renderCell: ({ row }) => <InvoiceLinks lines={row.visit?.invoiceLines ?? []} />,
  },
  {
    field: "explanation",
    headerName: "Why",
    flex: 1,
    minWidth: 170,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => (
      <Tooltip
        title={[row.explanation, ...row.issues.map((issue) => issue.clarification)].join(" ")}
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "normal",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
          }}
        >
          {row.explanation}
        </Typography>
      </Tooltip>
    ),
  },
];

/**
 * A URL-safe key: occurrence id, first invoice line id, and kind (which
 * separates the two results one event produces). Either half may be absent,
 * never both. Grading is deterministic, so the same period yields the same key.
 */
export function resultKey(row: Exception): string {
  return [row.occurrence?.id ?? "-", row.visit?.invoiceLines[0]?.id ?? "-", row.kind ?? "-"].join(
    "~"
  );
}

export const getItems = (data: WasteScheduleAdherenceQuery): ExceptionRow[] =>
  data.wasteScheduleAdherence.items.map((row) => ({ ...row, id: resultKey(row) }));

export const getCount = (data: WasteScheduleAdherenceQuery): number =>
  data.wasteScheduleAdherence.count;

/**
 * References a reader chases a row with rather than reads it by, so they ship
 * hidden and are turned on from Manage Columns when a row needs following up.
 * The billed amount is the same: it sizes a finding, it does not grade one.
 */
export const DEFAULT_COLUMN_VISIBILITY = {
  billedAmount: false,
  serviceOrderNumber: false,
  manifestNumber: false,
  invoices: false,
};
